<template>
  <v-dialog v-model="editClassDialog" max-width="800">
    <v-card class="schedule-class-dialog__card font-roboto rounded-lg" elevation="0" outlined>
      <v-card-text class="pa-6 pb-2">
        <div class="d-flex justify-space-between align-start mb-6">
          <div class="pr-2">
            <h2 class="font-roboto mb-1 plusGreyDarken4--text" style="font-size: 1.5rem;">
              {{ dialogTitle }}
            </h2>
            <p v-if="showAddSubtitle" class="font-roboto plusGreyDarken2--text text-subtitle-1 mb-0">
              {{ $t('message.add_class_subtitle') }}
            </p>
          </div>
          <v-btn icon small class="mt-n1" aria-label="Close" @click="showAddClassParams(true)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="addingClass" class="mb-6">
          <v-select v-model="isRecurringClass" hide-details outlined dense :items="classScheduleTypeItems"
            item-text="text" item-value="value" :label="$t('message.schedule_class_type')"
            @change="onRecurringTypeChange" />
        </div>

        <div class="mb-6">
          <v-text-field v-model="newClass.name" hide-details outlined dense :label="$t('message.class_title')" />
        </div>

        <v-row class="schedule-class-dialog__day-time-row mb-6">
          <v-col cols="12" md="5">
            <v-select v-if="addingClass" v-model="newClass.day" hide-details outlined dense :items="daysSelector"
              item-text="text" item-value="value" :label="$t('message.class_day')" :multiple="isRecurringClass" />
            <v-select v-else v-model="newClass.day" hide-details outlined dense disabled :items="daysSelector"
              item-text="text" item-value="value" :label="$t('message.class_day')" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="newClass.start" hide-details outlined dense :items="hours" item-text="display"
              item-value="value" :label="$t('message.class_start')" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="newClass.end" hide-details outlined dense :items="hours" item-text="display"
              item-value="value" :label="$t('message.class_end')" />
          </v-col>
        </v-row>

        <div class="mb-4">
          <v-autocomplete v-model="newClass.professorUser" hide-details :items="membersList"
            :label="$t('message.professor') + ' (' + $t('message.optional') + ')'" item-text="text" item-value="value"
            clearable outlined dense />
        </div>
        <div class="mb-4">
          <v-autocomplete v-model="newClass.assistantProfessors" multiple small-chips hide-details :items="membersList"
            :label="$t('message.assistant_professors') + ' (' + $t('message.optional') + ')'" item-text="text"
            item-value="value" clearable outlined dense />
        </div>

        <div class="mb-4">
          <v-textarea v-model="newClass.details" hide-details outlined dense rows="4"
            :label="$t('message.description') + ' (' + $t('message.optional') + ')'" />
        </div>

        <div class="d-flex align-start mb-4">
          <v-switch v-model="newClass.acceptVisitors" hide-details class="mt-1" inset dense color="plusIndigoDarken4" />
          <div>
            <div class="text-subtitle-1 plusGreyDarken1--text">{{ $t('message.accept_visitors') }}</div>
            <div class="font-roboto text-subtitle-2 plusGreyDarken1--text font-weight-regular">
              {{ $t('message.accept_visitors_explained') }}
            </div>
          </div>
        </div>
        <div class="d-flex align-start mb-4">
          <v-switch v-model="newClass.free" hide-details class="mt-1" inset dense color="plusIndigoDarken4" />
          <div>
            <div class="text-subtitle-1 plusGreyDarken1--text">{{ $t('message.free') }}</div>
            <div class="font-roboto text-subtitle-2 plusGreyDarken1--text font-weight-regular">
              {{ $t('message.free_class_explained') }}
            </div>
          </div>
        </div>
        <div class="d-flex align-start mb-2">
          <v-switch v-model="requireRegistration" hide-details class="mt-1" inset dense color="plusIndigoDarken4" />
          <div class="flex-grow-1">
            <div class="text-subtitle-1 plusGreyDarken1--text">{{ $t('message.require_registration') }}</div>
            <div class="font-roboto text-subtitle-2 plusGreyDarken1--text font-weight-regular">
              {{ $t('message.require_registration_explained') }}
            </div>
            <div v-if="requireRegistration" class="mt-3">
              <v-text-field v-model="newClass.maxCapacity" hide-details outlined dense
                :label="$t('message.max_capacity')" />
            </div>
          </div>
        </div>

        <!-- <div class="d-flex align-start mb-2 mt-4">
          <v-switch v-model="showColorPicker" hide-details class="mt-1" inset dense color="plusIndigoDarken4" />
          <div class="flex-grow-1">
            <div class="text-subtitle-1 plusGreyDarken1--text">{{ $t('message.class_color') }}</div>
            <div class="font-roboto text-subtitle-2 plusGreyDarken1--text font-weight-regular">
              {{ $t('message.class_color_explained') }}
            </div>
            <v-color-picker v-if="showColorPicker" v-model="newClass.color" class="mt-3" dot-size="25" hide-mode-switch
              hide-inputs mode="hexa" show-swatches swatches-max-height="150" />
          </div>
        </div> -->

        <div v-if="error" class="error--text mt-2">{{ $t('message.fill_all_params') }}</div>
      </v-card-text>

      <v-card-actions class="px-6 pb-6 pt-0 justify-start d-flex flex-wrap" style="gap: 12px;">

        <PlusButton :block="isSmallerDisplay()" small elevation="0" @click="showAddClassParams(true)">
          <span class="font-roboto text-button d-inline">{{ $t('message.cancel') }}</span>
        </PlusButton>

        <PlusButton :block="isSmallerDisplay()" v-if="!editingClass" :loading="processing" small intent="primary"
          @click="addToSchedule()">
          <span class="font-roboto text-button d-inline">{{ $t('message.add_class') }}</span>
        </PlusButton>

        <PlusButton :block="isSmallerDisplay()" v-else :loading="processing" small intent="primary"
          @click="updateInSchedule()">
          <span class="font-roboto text-button d-inline">{{ $t('message.update') }}</span>
        </PlusButton>
      </v-card-actions>
      <!-- <div class="text-caption plusGreyDarken1--text px-6 pb-4">
        {{ $t('message.only_editing_one_class') }}
      </div> -->
    </v-card>
  </v-dialog>
</template>

<script>
import Api from '@/services/Api';
import PlusButton from '../PlusButton.vue';
import { completeOnboardingStep } from "@/services/onboardingSteps";

const EMPTY_SCHEDULE = () => ({
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: []
});

export default {
  name: 'CalendarAdminPlusScheduleClassDialog',
  components: {
    PlusButton
  },
  data() {
    return {
      newClass: {},
      schedule: EMPTY_SCHEDULE(),
      members: [],
      addingClass: false,
      editingClass: false,
      processing: false,
      requireRegistration: false,
      showColorPicker: false,
      error: false,
      editClassDialog: false,
      contextLoading: false,
      isRecurringClass: true,
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      daysSelector: [
        { text: this.$t('message.monday'), value: 'monday' },
        { text: this.$t('message.tuesday'), value: 'tuesday' },
        { text: this.$t('message.wednesday'), value: 'wednesday' },
        { text: this.$t('message.thursday'), value: 'thursday' },
        { text: this.$t('message.friday'), value: 'friday' },
        { text: this.$t('message.saturday'), value: 'saturday' },
        { text: this.$t('message.sunday'), value: 'sunday' }
      ],
      hours: this.initializeHours()
    };
  },
  computed: {
    membersList() {
      return this.members && this.members.length ? this.members : [];
    },
    classScheduleTypeItems() {
      return [
        { text: this.$t('message.schedule_class_type_recurring'), value: true },
        { text: this.$t('message.schedule_class_type_single_day'), value: false }
      ];
    },
    dialogTitle() {
      if (this.editingClass) {
        return `${this.$t('message.edit')} ${this.$t('message.class')}`;
      }
      return this.$t('message.add_class');
    },
    showAddSubtitle() {
      return this.addingClass && !this.editingClass;
    }
  },
  methods: {
    onRecurringTypeChange() {
      const day = this.newClass.day;
      if (this.isRecurringClass) {
        if (day != null && !Array.isArray(day)) {
          this.$set(this.newClass, 'day', [day]);
        }
      } else if (Array.isArray(day)) {
        this.$set(this.newClass, 'day', day.length ? day[0] : null);
      }
    },
    initializeHours() {
      const hours = [];
      for (let totalMinutes = 5 * 60; totalMinutes < 24 * 60; totalMinutes += 5) {
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        const display = `${h}:${String(m).padStart(2, '0')}`;
        const value = parseFloat(`${h}.${String(m).padStart(2, '0')}`);
        hours.push({ display: display, value: value });
      }
      return hours;
    },
    ensureMembersLoaded() {
      if (this.members.length > 0) {
        return Promise.resolve();
      }
      return Api()
        .get('admins/academy/' + this.$store.state.selectedAcademy + '/members')
        .then((response) => {
          const data = response.data;
          if (data && data.approved) {
            this.members = data.approved.map((m) => ({
              value: m._id,
              text: m.fName + ' ' + m.lName
            }));
          }
        });
    },
    checkScheduleIds() {
      let fixSchedule = false;
      const days = Object.keys(this.schedule);
      if (days && days.length > 0) {
        for (const day of days) {
          const dayClasses = this.schedule[day];
          if (!dayClasses) continue;
          for (const c of dayClasses) {
            if (!c._id) fixSchedule = true;
            break;
          }
        }
      }
      if (fixSchedule) {
        this.updateSchedule(true);
      }
      return fixSchedule;
    },
    loadSchedule() {
      return Api()
        .get('admins/academy/' + this.$store.state.managedAcademy + '/schedule')
        .then((response) => {
          const data = response.data;
          if (data && data.schedule) {
            this.schedule = data.schedule;
          } else {
            this.schedule = EMPTY_SCHEDULE();
          }
          const needsFixing = this.checkScheduleIds();
          if (needsFixing) {
            const vm = this;
            return new Promise((resolve) => {
              setTimeout(() => {
                vm.loadSchedule().then(resolve).catch(resolve);
              }, 1000);
            });
          }
        });
    },
    showAddClassParams(clear) {
      this.editingClass = false;
      this.newClass = {};
      this.newClass.name = null;
      this.newClass.day = null;
      this.newClass.start = null;
      this.newClass.end = null;
      this.newClass.details = null;
      this.newClass.color = null;
      this.requireRegistration = false;
      this.showColorPicker = false;
      this.error = false;
      this.isRecurringClass = true;
      if (clear) {
        this.editClassDialog = false;
      } else {
        this.editClassDialog = true;
        this.addingClass = true;
      }
    },
    async openAdd() {
      if (this.contextLoading) {
        return;
      }
      this.contextLoading = true;
      try {
        await this.ensureMembersLoaded();
        await this.loadSchedule();
        this.showAddClassParams(false);
      } catch (e) {
        if (e) {
          this.showAlert('error', 'Oops!', this.$t('message.error_generic'));
        }
      } finally {
        this.contextLoading = false;
      }
    },
    addToSchedule() {
      this.error = false;
      if (!this.newClass.name || !this.newClass.day || !this.newClass.start || !this.newClass.end) {
        this.error = true;
        return;
      }
      if (Array.isArray(this.newClass.day)) {
        for (const day of this.newClass.day) {
          let dayClasses = this.schedule[day];
          if (!dayClasses) dayClasses = [];
          dayClasses.push({
            name: this.newClass.name,
            day: day,
            start: this.newClass.start,
            end: this.newClass.end,
            professorUser: this.newClass.professorUser,
            assistantProfessors: this.newClass.assistantProfessors,
            details: this.newClass.details,
            acceptVisitors: this.newClass.acceptVisitors,
            maxCapacity: this.newClass.maxCapacity,
            free: this.newClass.free,
            color: this.showColorPicker ? this.newClass.color?.hex || this.newClass.color : null
          });
          this.schedule[day] = this.sortedArray(dayClasses);
        }
      } else {
        let dayClasses2 = this.schedule[this.newClass.day];
        if (!dayClasses2) dayClasses2 = [];
        dayClasses2.push({
          name: this.newClass.name,
          day: this.newClass.day,
          start: this.newClass.start,
          end: this.newClass.end,
          professorUser: this.newClass.professorUser,
          assistantProfessors: this.newClass.assistantProfessors,
          details: this.newClass.details,
          acceptVisitors: this.newClass.acceptVisitors,
          maxCapacity: this.newClass.maxCapacity,
          free: this.newClass.free,
          color: this.showColorPicker ? this.newClass.color?.hex || this.newClass.color : null
        });
        this.schedule[this.newClass.day] = this.sortedArray(dayClasses2);
      }
      this.newClass = {};
      this.addingClass = false;
      this.updateSchedule();
    },
    updateInSchedule() {
      this.error = false;
      const classData = this.newClass;
      if (!this.newClass.name || !this.newClass.day || !this.newClass.start || !this.newClass.end) {
        this.error = true;
        return;
      }
      const dayClasses = this.schedule[classData.day];
      for (const c of dayClasses) {
        if (c._id == classData._id) {
          c.professorUser = classData.professorUser;
          c.assistantProfessors = classData.assistantProfessors;
          c.acceptVisitors = classData.acceptVisitors;
          c.details = classData.details;
          c.start = classData.start;
          c.end = classData.end;
          c.name = classData.name;
          c.maxCapacity = classData.maxCapacity;
          c.free = classData.free;
          c.color = this.showColorPicker ? classData.color?.hex || classData.color : null;
          if (!this.requireRegistration) c.maxCapacity = null;
          break;
        }
      }
      this.newClass = {};
      this.addingClass = false;
      this.editClassDialog = false;
      this.updateSchedule();
    },
    compare(a, b) {
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      return 0;
    },
    sortedArray(array) {
      if (array) {
        return array.sort(this.compare);
      }
      return array;
    },
    updateSchedule(skipAlert) {
      try {
        this.processing = true;
        const data = [
          {
            propName: 'schedule',
            value: this.schedule
          }
        ];
        Api()
          .patch('admins/academy/' + this.$store.state.selectedAcademy, {
            values: data
          })
          .then((response) => {
            const res = response.data;
            if (res) {
              this.editingClass = false;
              this.editClassDialog = false;
              this.newClass = {};
              this.requireRegistration = false;
              if (!skipAlert) this.showAlert('success', this.$t('message.updated'));
              completeOnboardingStep({
                step: "setupSchedule",
                managedAcademy: this.$store.state.managedAcademy,
                store: this.$store
              });

              this.$emit('saved');
            }
            this.processing = false;
          })
          .catch((e) => {
            if (e) this.showAlert('error', 'Oops!', this.$t('message.error_generic'));
            this.processing = false;
          });
      } catch (e) {
        console.log(e);
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
.schedule-class-dialog__card {
  border-color: rgba(0, 0, 0, 0.08) !important;
}
</style>
